import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { APIError, api } from '@/lib/api';
import { AuthForm } from './AuthForm';
import { AuthShell } from './AuthShell';
import { validatePassword } from './validation';

const MIN_PASSWORD = 8;

function tokenFromUrl(): string {
  return new URLSearchParams(window.location.search).get('token') ?? '';
}

interface Errors {
  password?: string | undefined;
  confirm?: string | undefined;
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [token] = useState(tokenFromUrl);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);

  function validate(): boolean {
    const next: Errors = {
      password: validatePassword(password, { min: MIN_PASSWORD }),
      confirm: confirm !== password ? 'Passwords do not match.' : undefined,
    };
    setErrors(next);
    return !next.password && !next.confirm;
  }

  async function submit() {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await api.auth.resetPassword({ token, password });
      toast.success(res.message);
      await navigate({ to: '/login' });
    } catch (err) {
      toast.error(err instanceof APIError ? err.detail : 'Could not reset password');
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <AuthShell title="Invalid reset link" subtitle="">
        <p className="text-center text-sm text-muted-foreground">
          This password reset link is missing or malformed. Request a new one.
        </p>
        <div className="mt-6 text-center text-sm text-muted-foreground">
          <Link to="/forgot-password" className="font-medium text-foreground hover:underline">
            Request a new link
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthForm
      title="Set a new password"
      subtitle="Choose a strong password you don't use elsewhere"
      submitLabel="Update password"
      submitting={submitting}
      onSubmit={submit}
      fields={[
        {
          id: 'password',
          label: `New password (min ${MIN_PASSWORD} characters)`,
          type: 'password',
          value: password,
          onChange: (v) => {
            setPassword(v);
            setErrors((e) => ({ ...e, password: undefined }));
          },
          autoComplete: 'new-password',
          placeholder: '••••••••',
          error: errors.password,
          required: true,
        },
        {
          id: 'confirm',
          label: 'Confirm new password',
          type: 'password',
          value: confirm,
          onChange: (v) => {
            setConfirm(v);
            setErrors((e) => ({ ...e, confirm: undefined }));
          },
          autoComplete: 'new-password',
          placeholder: '••••••••',
          error: errors.confirm,
          required: true,
        },
      ]}
      footer={
        <Link to="/login" className="font-medium text-foreground hover:underline">
          Back to login
        </Link>
      }
    />
  );
}
